import _isNil from 'lodash/isNil';
import _isString from 'lodash/isString';
import _uniqBy from 'lodash/uniqBy';

export class Version {
  public readonly displayValue: string;
  public readonly major: number | null = null;
  public readonly minor: number | null = null;
  public readonly patch: number | null = null;

  constructor(rawVersionValue: string) {
    this.displayValue = rawVersionValue;

    if (
      _isNil(rawVersionValue) ||
      !_isString(rawVersionValue) ||
      !rawVersionValue
    ) {
      return;
    }

    const parts = rawVersionValue.split('.');
    if (!parts.length || parts.find(_ => isNaN(_ as any))) return;

    this.major = Number(parts[0]) || 0;
    this.minor = Number(parts[1]) || 0;
    this.patch = Number(parts[2]) || 0;
  }

  public get isValid() {
    return [this.major, this.minor, this.patch].every(_ => _ !== null);
  }

  sortWith(otherVersion: Version): number {
    if (!this.isValid) return 0;

    if (this.major !== otherVersion.major) {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return this.major - otherVersion.major;
    } else if (this.minor !== otherVersion.minor) {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return this.minor - otherVersion.minor;
    } else {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return this.patch - otherVersion.patch;
    }
  }

  get majorDisplay() {
    return `${this.major}`;
  }

  isValueSameMajorVersion(rawValue: string): boolean {
    const asVersion = new Version(rawValue);
    return asVersion.major === this.major;
  }
}

export default class Versions {
  public readonly versions: Version[];
  constructor(rawVersions: string[]) {
    this.versions = (rawVersions || [])
      .map(_ => new Version(_))
      .filter(_ => _.isValid)
      .sort((v1, v2) => v1.sortWith(v2));
  }

  get majorVersions(): Version[] {
    const unique = _uniqBy(this.versions, _ => _.major);
    return unique.sort((v1, v2) => v1.sortWith(v2));
  }

  getVersionsForMajorVersion(rawVersion: string): Version[] {
    const asVersion = new Version(rawVersion);
    return this.versions
      .filter(_ => _.major === asVersion.major)
      .sort((v1, v2) => v1.sortWith(v2));
  }
}
